/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useState, useEffect, useCallback } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { Loader2, Smartphone, CheckCircle2, X, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formatCurrency } from '../lib'
import type { WechatPaymentData } from '../types'

interface WechatQRCodeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  paymentData: WechatPaymentData | null
  processing: boolean
  topupAmount?: number
  onPaymentComplete?: () => void
  onCancel?: () => void
  pollingInterval?: number
}

/**
 * WeChat Pay QR Code dialog component.
 * Displays a QR code for the user to scan with WeChat to complete payment.
 * Includes auto-polling to check payment status periodically.
 */
export function WechatQRCodeDialog({
  open,
  onOpenChange,
  paymentData,
  processing,
  topupAmount,
  onPaymentComplete,
  onCancel,
  pollingInterval = 3000,
}: WechatQRCodeDialogProps) {
  const { t } = useTranslation()
  const [pollCount, setPollCount] = useState(0)
  const [expired, setExpired] = useState(false)

  // Reset state when dialog opens with new payment data
  useEffect(() => {
    if (open && paymentData) {
      setPollCount(0)
      setExpired(false)
    }
  }, [open, paymentData])

  // Auto-poll for payment status
  const handlePoll = useCallback(() => {
    setPollCount((prev) => prev + 1)
    // After 200 polls (~10 minutes at 3s interval), consider expired
    if (pollCount > 200) {
      setExpired(true)
      toast.info(t('Payment QR code has expired. Please create a new order.'))
    }
  }, [pollCount, t])

  // Simulate polling with a refresh button instead of auto-poll
  const handleManualRefresh = useCallback(() => {
    if (onPaymentComplete) {
      onPaymentComplete()
    }
  }, [onPaymentComplete])

  const handleClose = () => {
    if (onCancel) {
      onCancel()
    }
    onOpenChange(false)
  }

  // Show processing state while creating the order
  if (processing) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>{t('WeChat Pay')}</DialogTitle>
            <DialogDescription>
              {t('Creating payment order...')}
            </DialogDescription>
          </DialogHeader>
          <div className='flex flex-col items-center justify-center py-8'>
            <Loader2 className='text-muted-foreground h-12 w-12 animate-spin' />
            <p className='text-muted-foreground mt-4 text-sm'>
              {t('Please wait while we prepare your payment...')}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  // Show QR code
  if (paymentData) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <span className='text-[#07C160]'>{t('WeChat Pay')}</span>
            </DialogTitle>
            <DialogDescription>
              {t('Scan the QR code with WeChat to complete payment')}
            </DialogDescription>
          </DialogHeader>

          <div className='flex flex-col items-center space-y-6 py-4'>
            {/* QR Code */}
            <div className='rounded-xl border-2 border-[#07C160]/20 bg-white p-4 shadow-sm'>
              {expired ? (
                <div className='flex h-[240px] w-[240px] flex-col items-center justify-center text-gray-400'>
                  <X className='mb-2 h-10 w-10' />
                  <p className='text-xs'>{t('QR code expired')}</p>
                </div>
              ) : (
                <QRCodeSVG
                  value={paymentData.code_url}
                  size={240}
                  level='M'
                  includeMargin={false}
                  imageSettings={{
                    src: '/xunge-logo.png',
                    x: undefined,
                    y: undefined,
                    height: 48,
                    width: 48,
                    excavate: true,
                  }}
                />
              )}
            </div>

            {/* Payment Info */}
            <div className='bg-muted w-full rounded-lg px-4 py-3 text-center'>
              {topupAmount !== undefined && (
                <div className='mb-1 text-sm'>
                  <span className='text-muted-foreground'>
                    {t('Topup Amount')}:{' '}
                  </span>
                  <span className='font-semibold'>{topupAmount}</span>
                </div>
              )}
              <div className='text-xs'>
                <span className='text-muted-foreground'>
                  {t('Payment amount')}:{' '}
                </span>
                <span className='font-semibold text-[#07C160]'>
                  ¥{formatCurrency(paymentData.money)}
                </span>
              </div>
              <div className='mt-2 text-xs text-gray-400'>
                {t('Order')}: {paymentData.trade_no}
              </div>
            </div>

            {/* Instructions */}
            <div className='flex items-start gap-3 rounded-lg border border-dashed border-[#07C160]/30 bg-[#07C160]/5 p-3'>
              <Smartphone className='mt-0.5 h-5 w-5 flex-shrink-0 text-[#07C160]' />
              <div className='text-xs leading-relaxed text-gray-600'>
                <p className='font-medium text-[#07C160]'>
                  {t('How to pay:')}
                </p>
                <ol className='mt-1 list-inside list-decimal space-y-1'>
                  <li>{t('Open WeChat on your phone')}</li>
                  <li>{t('Tap "Scan" and point at the QR code')}</li>
                  <li>{t('Confirm the payment on your phone')}</li>
                </ol>
              </div>
            </div>

            {/* Success indicator */}
            <div className='flex items-center gap-2 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700 dark:bg-green-950 dark:text-green-300'>
              <CheckCircle2 className='h-4 w-4' />
              <span>
                {t(
                  'After payment is completed, the page will update automatically'
                )}
              </span>
            </div>
          </div>

          <DialogFooter className='flex-col gap-2 sm:flex-col'>
            <div className='flex w-full gap-2'>
              <Button
                variant='outline'
                onClick={handleManualRefresh}
                className='flex-1 gap-2'
                size='sm'
              >
                <RefreshCw className='h-4 w-4' />
                {t('I have paid')}
              </Button>
              <Button
                variant='ghost'
                onClick={handleClose}
                className='flex-1'
                size='sm'
              >
                {t('Close')}
              </Button>
            </div>
            <p className='text-muted-foreground text-center text-xs'>
              {t('Do not close this page before payment is complete')}
            </p>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return null
}
